import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles, Plus, Globe } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { refresh, setActive } = useProjects();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [description, setDescription] = useState("");
  const [language, setLanguage] = useState("English");
  const [project, setProject] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const createProject = async (e) => {
    e?.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/projects", {
        name, domain, description: description || null, primary_language: language,
      });
      setProject(data);
      await refresh();
      setActive(data.id);
      setStep(2);
      // auto-trigger analysis
      runAnalysis(data.id);
    } catch (e2) {
      setError(formatApiError(e2));
      setLoading(false);
    }
  };

  const runAnalysis = async (projectId) => {
    setLoading(true); setError("");
    try {
      const { data } = await api.post(`/projects/${projectId}/analyze`, {
        domain, description: description || null,
      });
      setAnalysis(data.result);
      setStep(3);
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout
      eyebrow="Onboarding"
      title="Let's analyze your business."
      subtitle="Add your site and we'll build a personalized growth plan in under a minute."
    >
      {/* Progress */}
      <div className="flex items-center gap-2 mb-8" data-testid="onboarding-stepper">
        {["Site", "Analysis", "Plan"].map((label, i) => {
          const idx = i + 1;
          const active = step === idx;
          const done = step > idx;
          return (
            <div key={label} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-sm flex items-center justify-center text-sm font-mono border ${
                active ? "border-[#0055FF] bg-[#0055FF]/10 text-white" :
                done ? "border-[#10B981] bg-[#10B981]/10 text-[#10B981]" :
                "border-zinc-800 text-zinc-500"
              }`}>{done ? "✓" : idx}</div>
              <span className={`text-sm ${active ? "text-white" : "text-zinc-500"}`}>{label}</span>
              {idx < 3 && <div className={`w-12 h-px ${done ? "bg-[#10B981]" : "bg-zinc-800"}`} />}
            </div>
          );
        })}
      </div>

      {step === 1 && (
        <form onSubmit={createProject} className="bg-zinc-950 border border-zinc-800 rounded-sm p-8 max-w-2xl space-y-5" data-testid="onboarding-form-step-1">
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Site name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="My SaaS"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="onboarding-name" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Domain</Label>
            <Input value={domain} onChange={(e) => setDomain(e.target.value)} required placeholder="mysite.com"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="onboarding-domain" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Short description (optional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                      placeholder="What does your business do? Who are your customers?"
                      className="bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="onboarding-description" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Primary language</Label>
            <Input value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="English, Spanish, French..."
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="onboarding-language" />
          </div>
          {error && <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="onboarding-error">{error}</div>}
          <Button type="submit" disabled={loading} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11 px-6" data-testid="onboarding-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> Analyze my business</>}
          </Button>
        </form>
      )}

      {step === 2 && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-12 text-center max-w-2xl" data-testid="onboarding-analyzing">
          <Loader2 className="w-10 h-10 mx-auto text-[#0055FF] animate-spin mb-4" />
          <h3 className="font-display text-2xl font-medium">Analyzing {domain}...</h3>
          <p className="text-zinc-400 mt-2">GPT-5.2 is profiling your business, mapping competitors, and surfacing your fastest wins.</p>
        </div>
      )}

      {step === 3 && analysis && (
        <div className="space-y-6 fade-in" data-testid="onboarding-result">
          {/* Summary */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-8">
            <span className="label-eyebrow">Business profile</span>
            <h2 className="font-display text-2xl font-medium mt-2">{name} · {analysis.industry}</h2>
            <p className="text-zinc-300 mt-3 leading-relaxed">{analysis.business_summary}</p>
            <div className="mt-6 grid md:grid-cols-2 gap-6">
              <Field label="Target audience" value={analysis.target_audience} />
              <Field label="Value proposition" value={analysis.value_proposition} />
            </div>
          </div>

          {/* Competitors */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6">
            <span className="label-eyebrow">Competitors detected</span>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {(analysis.competitors || []).map((c, i) => (
                <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm p-3" data-testid={`competitor-${i}`}>
                  <div className="text-zinc-200 text-sm font-medium truncate">{c.name}</div>
                  <div className="text-xs text-zinc-500 truncate flex items-center gap-1 mt-1"><Globe className="w-3 h-3" /> {c.domain}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Pillars + keywords */}
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 lg:col-span-1">
              <span className="label-eyebrow">Content pillars</span>
              <ul className="mt-4 space-y-2">
                {(analysis.content_pillars || []).map((p, i) => (
                  <li key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-200" data-testid={`pillar-${i}`}>{p}</li>
                ))}
              </ul>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 lg:col-span-2">
              <div className="flex justify-between items-baseline mb-3">
                <span className="label-eyebrow">High-priority keywords</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {(analysis.primary_keywords || []).map((k, i) => (
                  <Badge key={i} className="bg-[#0055FF]/10 text-[#3377FF] border-[#0055FF]/30 rounded-sm" data-testid={`kw-primary-${i}`}>{k}</Badge>
                ))}
              </div>
              <div className="mt-4">
                <span className="label-eyebrow block mb-2">Long-tail keywords</span>
                <div className="flex gap-2 flex-wrap">
                  {(analysis.secondary_keywords || []).map((k, i) => (
                    <Badge key={i} variant="outline" className="rounded-sm border-zinc-700 text-zinc-300" data-testid={`kw-secondary-${i}`}>{k}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Roadmap */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6" data-testid="roadmap">
            <span className="label-eyebrow">90-day growth roadmap</span>
            <div className="mt-4 grid md:grid-cols-3 gap-4">
              {[
                { title: "First 30 days", items: analysis.roadmap_30_60_90?.["30_days"], color: "#0055FF" },
                { title: "Days 31–60", items: analysis.roadmap_30_60_90?.["60_days"], color: "#10B981" },
                { title: "Days 61–90", items: analysis.roadmap_30_60_90?.["90_days"], color: "#F59E0B" },
              ].map((b, i) => (
                <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
                  <div className="text-xs uppercase tracking-widest mb-3" style={{ color: b.color }}>{b.title}</div>
                  <ul className="space-y-2">
                    {(b.items || []).map((it, j) => (
                      <li key={j} className="text-sm text-zinc-300 leading-relaxed flex gap-2">
                        <span className="text-zinc-600 font-mono text-xs mt-0.5">{j + 1}</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* GEO */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6">
            <span className="label-eyebrow">AI search opportunities (GEO)</span>
            <ul className="mt-4 space-y-2">
              {(analysis.geo_opportunities || []).map((g, i) => (
                <li key={i} className="text-sm text-zinc-300 flex gap-2" data-testid={`geo-${i}`}>
                  <Sparkles className="w-4 h-4 text-[#3377FF] mt-0.5 flex-shrink-0" /> {g}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button onClick={() => navigate("/autopilot")} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11" data-testid="onboarding-cta-autopilot">
              <Sparkles className="w-4 h-4 mr-2" /> Run full autopilot
            </Button>
            <Button onClick={() => navigate("/content-plan")} variant="outline" className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm h-11" data-testid="onboarding-cta-plan">
              <Plus className="w-4 h-4 mr-2" /> Generate content plan only
            </Button>
            <Button onClick={() => navigate("/dashboard")} variant="ghost" className="text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-sm h-11" data-testid="onboarding-cta-dashboard">
              Skip to dashboard
            </Button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <span className="label-eyebrow">{label}</span>
      <p className="text-sm text-zinc-300 mt-1.5 leading-relaxed">{value}</p>
    </div>
  );
}
